import {BrowserRouter as Router, Routes, Route, Navigate, useLocation} from 'react-router-dom'
import type {OktaAuth} from '@okta/okta-auth-js'
import {toRelativeUrl} from '@okta/okta-auth-js'
import {Security, LoginCallback, useOktaAuth} from '@okta/okta-react'
import {oktaAuth} from '@/config/oktaConfig' // Import the oktaAuth instance directly
import ProtectedRoute from '@/routes/ProtectedRoute'
import RoleBasedRedirect from '@/routes/RoleBasedRedirect'
import {Login, Logout} from '@/components/Auth'
import Layout from '@/layout/Layout'
import {ThemeProvider as PepsicoThemeProvider} from 'pepsico-ds'
import {FullstoryWrapper} from '@/components/FullStory'
import {persistor} from '@/store'
import {PersistGate} from 'redux-persist/integration/react'
import '@pepsico-ds/ui/css' // Ensure the pepsico-ds CSS is imported at the application level
import {useSessionManagement} from '@/hooks/useSessionManagement'
import {useUserTheme} from '@/hooks/useUserTheme'
import {useTokenRefresh} from '@/hooks/useTokenRefresh'
import {useThemeMode} from '@/hooks/useThemeMode'
import {type ReactElement, useEffect, Suspense} from 'react'
import {useDispatch} from 'react-redux'
import {fetchAccessSummary, selectAccessSummaryLoading} from '@/store/slices/accessSummarySlice'
import type {AppDispatch} from '@/store'
import {useFeatureFlag} from '@/contexts/GrowthBookContext'
import {lazyLoad} from '@/utils/helperMethods'
import LoadingSpinner from '@/components/GDS/atoms/LoadingSpinner'
import {GrowthBookProvider} from '@/contexts/GrowthBookContext'
import {GROWTHBOOK_CLIENT_KEY} from '@/config/envConfig'
import {getGrowthBookApiUrl} from '@/services/growthbookService'
import {syncAppIconWithTheme} from '@/utils/appIcon'
import MetricsSettings from './components/AdminSections/MetricsSettings'
import {AuthService} from '@/services/authService'
import NotFound from '@/components/common/NotFound'
import SEMetricsTrends from './components/SEMetricsTrends'
import {useSelector} from 'react-redux'
import {selectAccessSummary} from '@/store/slices/accessSummarySlice'

// Lazy load route-level components for better initial load performance
const TeamDashboard = lazyLoad(() => import('@/components/Dashboard/TeamDashboard/TeamDashboard'))
const ProgramDashboard = lazyLoad(() => import('@/components/Dashboard/ProgramDashboard/ProgramDashboard'))
const SEMetrics = lazyLoad(() => import('@/components/SEMetrics'))
const AIProductivityMetrics = lazyLoad(() => import('@/components/AIProductivityMetrics'))
const VendorAnalysis = lazyLoad(() => import('@/components/AIProductivityMetrics/VendorAnalysis'))
const VendorContributionInsights = lazyLoad(
  () => import('@/components/AIProductivityMetrics/VendorAnalysis/VendorContributionInsights'),
)
const AgileMetrics = lazyLoad(() => import('@/components/AgileMetrics'))
const CommitAnalyticsReport = lazyLoad(() => import('@/components/CommitAnalyticsReport'))
const Reports = lazyLoad(() => import('@/components/Reports'))
const CustomReportPage = lazyLoad(() => import('@/components/Reports/CustomReport'))
const GeneratedReportPage = lazyLoad(() => import('@/components/Reports/CustomReport/GeneratedReport'))
const TopProgramsReport = lazyLoad(() => import('@/components/TopProgramsReport'))
const EmdTrends = lazyLoad(() => import('@/components/EmdTrends/EmdTrends'))
const ManageRoles = lazyLoad(() => import('@/components/AdminSections/ManageRoles'))
const ManageAccess = lazyLoad(() => import('@/components/AdminSections/ManageAccess'))
const ProgramsAndConfigurations = lazyLoad(() => import('@/components/AdminSections/ProgramsAndConfigurations'))
const FAQs = lazyLoad(() => import('@/components/FAQ/FAQs'))
const Docs = lazyLoad(() => import('@/components/Docs/Docs'))
const AskDevLens = lazyLoad(() => import('@/components/AskDevLens'))
const NewDashboard = lazyLoad(() => import('@/components/Dashboard/NewDashboard/NewDashboard'))
const LandingPage = lazyLoad(() => import('@/components/Dashboard/LandingPage'))

const TopProgramsLegacyRedirect = (): ReactElement => {
  const location = useLocation()
  return <Navigate to={{pathname: '/engineering-leadership-insights', search: location.search}} replace />
}

const TeamDashboardOnboardingPageV2 = lazyLoad(
  () => import('@/components/SelfOnboarding/TeamsDashboardV2/TeamDashboardOnboardingPage'),
)
const ProgramDashboardOnboardingPageV2 = lazyLoad(
  () => import('@/components/SelfOnboarding/ProgramDashboardV2/ProgramDashboardOnboardingPage'),
)

// Define callback for redirecting after authentication
export const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string): Promise<void> => {
  try {
    // If no originalUri provided, default to home page
    const redirectUri = originalUri || '/'

    // Use toRelativeUrl to ensure proper formatting
    const relativeUrl = toRelativeUrl(redirectUri, window.location.origin)

    window.location.replace(relativeUrl)
  } catch (error) {
    console.error('Error in restoreOriginalUri:', error)
    window.location.href = '/'
  }
}

// Inner component that handles session management
const AppWithSessionManagement = (): ReactElement => {
  const {authState} = useOktaAuth()
  const dispatch = useDispatch<AppDispatch>()
  const enableTeamOnboardingV2 = useFeatureFlag('enable_team_onboarding_v2') ?? true
  const enableProgramOnboardingV2 = useFeatureFlag('enable_program_onboarding_v2') ?? true
  const enableAskDevLensChatbot = useFeatureFlag('ask-devlens-chatbot-available') === true
  const {startSessionMonitoring, stopSessionMonitoring, handle401Error} = useSessionManagement()
  const themeMode = useThemeMode()
  const accessSummary = useSelector(selectAccessSummary)
  const accessSummaryLoading = useSelector(selectAccessSummaryLoading)
  // Load user theme from API once on authentication
  useUserTheme()

  // Refresh backend token on page load to pick up role changes
  useTokenRefresh(authState?.isAuthenticated ?? false)

  useEffect(() => {
    syncAppIconWithTheme(themeMode)
  }, [themeMode])

  useEffect(() => {
    syncAppIconWithTheme(themeMode)
  }, [themeMode])

  useEffect(() => {
    const exchangeToken = async (): Promise<void> => {
      if (authState?.isAuthenticated === true) {
        const hasBackendToken = localStorage.getItem('access_token')

        if (hasBackendToken === null || hasBackendToken === '') {
          try {
            // Exchange token immediately
            const token = await AuthService.exchangeTokenForBackendToken()
            if (token !== null && token !== undefined && token !== '') {
              // Force a reload to ensure all components have access to the new token
              // This is a simple way to ensure everything is re-rendered with the correct permissions
              window.location.reload()
            } else {
              console.error('Token exchange failed - no token returned')
            }
          } catch (error) {
            console.error('Token exchange error:', error)
          }
        }
      }
    }

    // Run immediately
    exchangeToken()
  }, [authState?.isAuthenticated])

  useEffect(() => {
    // Make 401 handler globally available for interceptors
    ;(globalThis as Record<string, unknown>).handle401Error = handle401Error

    // Start session monitoring when user is authenticated
    if (authState?.isAuthenticated === true) {
      startSessionMonitoring()
      // Fetch access delegations summary at app load
      dispatch(fetchAccessSummary())
    } else {
      stopSessionMonitoring()
    }

    // Cleanup on unmount
    return (): void => {
      stopSessionMonitoring()
      delete (globalThis as Record<string, unknown>).handle401Error
    }
  }, [authState?.isAuthenticated, startSessionMonitoring, stopSessionMonitoring, handle401Error])

  const useNewDashboardAsLanding = useFeatureFlag('emd_new_dashboard_landing') === true
  const LandingComponent = useNewDashboardAsLanding ? NewDashboard : LandingPage

  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
        <Route
          path="/login/callback"
          element={
            <LoginCallback
              loadingElement={
                <div className="flex h-screen w-screen items-center justify-center bg-white">
                  <LoadingSpinner />
                </div>
              }
            />
          }
        />
        {/* Protected routes with shared layout */}
        <Route element={<ProtectedRoute />}>
          <Route
            index
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <RoleBasedRedirect>
                    <LandingComponent />
                  </RoleBasedRedirect>
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/programs-teams"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <RoleBasedRedirect>
                    <LandingPage />
                  </RoleBasedRedirect>
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/program/:programId/team/:id"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <TeamDashboard />
                </Suspense>
              </Layout>
            }
          />
          {/* Legacy route for backward compatibility */}
          <Route
            path="/team/:id"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <TeamDashboard />
                </Suspense>
              </Layout>
            }
          />
          {enableTeamOnboardingV2 && (
            <>
              <Route
                path="/team-dashboard/add"
                element={
                  <Layout>
                    <Suspense
                      fallback={
                        <div className="flex h-[400px] items-center justify-center">
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <TeamDashboardOnboardingPageV2 />
                    </Suspense>
                  </Layout>
                }
              />
              <Route
                path="/team-dashboard/edit"
                element={
                  <Layout>
                    <Suspense
                      fallback={
                        <div className="flex h-[400px] items-center justify-center">
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <TeamDashboardOnboardingPageV2 />
                    </Suspense>
                  </Layout>
                }
              />
            </>
          )}
          <Route
            path="/program/:id"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <ProgramDashboard />
                </Suspense>
              </Layout>
            }
          />
          {enableProgramOnboardingV2 && (
            <>
              <Route
                path="/program-dashboard/add"
                element={
                  <Layout>
                    <Suspense
                      fallback={
                        <div className="flex h-[400px] items-center justify-center">
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <ProgramDashboardOnboardingPageV2 />
                    </Suspense>
                  </Layout>
                }
              />
              <Route
                path="/program-dashboard/edit"
                element={
                  <Layout>
                    <Suspense
                      fallback={
                        <div className="flex h-[400px] items-center justify-center">
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <ProgramDashboardOnboardingPageV2 />
                    </Suspense>
                  </Layout>
                }
              />
            </>
          )}
          <Route
            path="/se-metrics"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <SEMetrics />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/ai-productivity"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <AIProductivityMetrics />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/vendor-analysis"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <VendorAnalysis />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/vendor-contribution-insights"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <VendorContributionInsights />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/agile-metrics"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <AgileMetrics />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/programs-configurations"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <ProgramsAndConfigurations />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/manage-access"
            element={
              <Layout>
                {accessSummaryLoading || accessSummary === null ? (
                  <div className="flex h-[400px] items-center justify-center">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <Suspense
                    fallback={
                      <div className="flex h-[400px] items-center justify-center">
                        <LoadingSpinner />
                      </div>
                    }
                  >
                    <ManageAccess />
                  </Suspense>
                )}
              </Layout>
            }
          />
          <Route
            path="/faqs"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <FAQs />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/docs/*"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <Docs />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/metrics-settings"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <MetricsSettings />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/manage-roles"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <ManageRoles />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/reports"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <Reports />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/reports/custom"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <CustomReportPage />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/reports/custom/generate"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <GeneratedReportPage />
                </Suspense>
              </Layout>
            }
          />
          <Route path="/top-programs" element={<TopProgramsLegacyRedirect />} />
          <Route
            path="/engineering-leadership-insights"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <TopProgramsReport />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/se-trends"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <SEMetricsTrends />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/commit-analytics"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <CommitAnalyticsReport />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/ask-devlens"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <AskDevLens />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="/trends"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[400px] items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  }
                >
                  <EmdTrends />
                </Suspense>
              </Layout>
            }
          />
        </Route>
        {/* Catch-all route - show NotFound page */}
        <Route
          path="*"
          element={
            <Layout>
              <NotFound
                title="Page Not Found"
                message="The page you are looking for does not exist or may have been moved."
              />
            </Layout>
          }
        />
      </Routes>
    </Router>
  )
}

const App = (): ReactElement => {
  // Get the environment-specific GrowthBook API URL
  const apiHost = getGrowthBookApiUrl()

  return (
    <PepsicoThemeProvider>
      <PersistGate loading={null} persistor={persistor}>
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
          <FullstoryWrapper>
            <GrowthBookProvider apiHost={apiHost} clientKey={GROWTHBOOK_CLIENT_KEY}>
              <AppWithSessionManagement />
            </GrowthBookProvider>
          </FullstoryWrapper>
        </Security>
      </PersistGate>
    </PepsicoThemeProvider>
  )
}

export default App
